import { type ReactNode, useEffect } from 'react';
import { ChevronLeft, Pencil } from 'lucide-react';
import { Money, Tag } from '@/components/ui/badges';
import { stageLabel } from '@/lib/domain/stages';
import { useDealDrawer } from '@/features/pipeline/dealDrawerStore';
import type { Deal } from '@/features/pipeline/types';
import type { ProfileDealItem } from './profileApi';

const AUX_LABEL: Record<string, string> = {
  Active: 'Ativo',
  Paused: 'Em pausa',
  Discarded: 'Descartado',
  ClosedToPortfolio: 'No portfólio',
};

/** Constrói um Deal mínimo a partir do item da ficha para abrir o DealDrawer (que enriquece via GET). */
function dealFromProfileItem(item: ProfileDealItem): Deal {
  return {
    id: item.dealId,
    code: item.code,
    codename: item.codename,
    stage: item.currentStage,
    auxiliaryStatus: item.auxiliaryStatus,
    legalStatus: 'Nenhum',
    stageEnteredAtUtc: new Date().toISOString(),
    initialEntryAtUtc: new Date().toISOString(),
    leadUserId: '',
    leadUserName: item.leadUserName ?? '—',
    investmentValueBRL: item.investmentValueBRL,
    checkDiscretionaryBRL: null,
    checkCoinvestmentBRL: null,
    isPriority: false,
    companyId: '',
    companyName: '—',
    thesisNames: [],
    teamUserNames: [],
    etag: '',
  };
}

/** Casca do drawer de ficha (Originadora/Pessoa) — painel à direita, espelha o DealDrawer (z menor). */
export function ProfileDrawerShell({
  kindLabel,
  code,
  title,
  subtitle,
  onClose,
  onEdit,
  editing,
  children,
}: {
  kindLabel: string;
  code?: string;
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  onEdit?: () => void;
  editing: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="fixed inset-0 z-[45] cursor-default bg-[rgba(20,18,12,0.35)]"
      />
      <aside
        role="dialog"
        aria-label={`${kindLabel}: ${title}`}
        className="fixed inset-y-0 right-0 z-[48] flex w-[min(720px,92vw)] flex-col bg-vega-surface shadow-vega-lg"
      >
        <div className="border-b border-vega-line px-6 pt-3.5 pb-3.5">
          <div className="mb-2 flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="-ml-1 inline-flex rounded p-1 text-vega-muted hover:bg-vega-bg-2"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-[11px] font-medium uppercase tracking-wide text-vega-muted">{kindLabel}</span>
            {code && <span className="font-mono text-[11px] tracking-wide text-vega-muted-2">{code}</span>}
            <div className="flex-1" />
            {onEdit && !editing && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 rounded border border-vega-line px-2 py-1 text-[11px] text-vega-muted"
              >
                <Pencil size={12} /> Editar
              </button>
            )}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight text-vega-ink">{title}</h1>
          {subtitle && <div className="mt-1 text-[12.5px] text-vega-muted">{subtitle}</div>}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
      </aside>
    </>
  );
}

/** Linha de info (rótulo + valor) da ficha. */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-[12.5px]">
      <span className="w-[140px] flex-shrink-0 text-vega-muted">{label}</span>
      <span className="flex-1 text-vega-ink-2">{children ?? '—'}</span>
    </div>
  );
}

/** Seção de KPIs da ficha (4 métricas da originadora). */
export function MetricsStrip({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div className="mb-5 grid grid-cols-2 gap-px border border-vega-line-2 bg-vega-line-2 sm:grid-cols-4">
      {items.map((m) => (
        <div key={m.label} className="bg-vega-surface px-3 py-2.5">
          <div className="mb-1 text-[10px] uppercase tracking-wide text-vega-muted">{m.label}</div>
          <span className="font-mono text-[18px] font-semibold text-vega-ink">{m.value}</span>
        </div>
      ))}
    </div>
  );
}

/** Lista de deals da ficha — cada linha abre o DealDrawer. */
export function ProfileDealsList({ deals }: { deals: ProfileDealItem[] }) {
  const openDeal = useDealDrawer((s) => s.openDeal);
  if (deals.length === 0) return <div className="py-4 text-[12.5px] text-vega-muted-2">Nenhum deal vinculado.</div>;
  return (
    <table className="w-full border-collapse text-[12.5px]">
      <thead>
        <tr>
          {['Código', 'Codinome', 'Fase', 'Status', 'Cheque', 'Responsável'].map((h, i) => (
            <th
              key={h}
              className={`border-b border-vega-line px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-vega-muted ${
                i === 4 ? 'text-right' : 'text-left'
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {deals.map((d) => (
          <tr
            key={d.dealId}
            onClick={() => openDeal(dealFromProfileItem(d))}
            className="cursor-pointer border-b border-vega-line-2 hover:bg-vega-surface-2"
          >
            <td className="px-2 py-2 font-mono text-[11px] text-vega-muted-2">{d.code}</td>
            <td className="px-2 py-2 font-medium text-vega-ink">{d.codename}</td>
            <td className="px-2 py-2 text-vega-ink-2">{stageLabel(d.currentStage)}</td>
            <td className="px-2 py-2"><Tag size="sm">{AUX_LABEL[d.auxiliaryStatus] ?? d.auxiliaryStatus}</Tag></td>
            <td className="px-2 py-2 text-right"><Money value={d.investmentValueBRL} /></td>
            <td className="px-2 py-2 text-vega-muted">{d.leadUserName ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
